/**
 * @fileoverview Weekly schedule ("Studio Quiet"): a hand-rolled CSS-grid week
 * view of materialized occurrences (Spec 04 §7 — no calendar library), with
 * slot create/edit and per-occurrence skip/move/undo exceptions. Calendar
 * sync lives on the Calendar page and in Settings (Spec 19). Occurrence identity is always (slot_id, date); a
 * moved occurrence renders at its effective date/time with a "moved" tag.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";
import api, { getErrorMessage } from "@/lib/api";
import { MARKETPLACE_ENABLED, noShowEnabled, studioLayerEnabled } from "@/lib/featureFlags";
import { tierDisplayName } from "@/lib/tiers";
import {
  LESSON_TYPE_LABELS,
  WEEKDAYS,
  WEEKDAYS_SHORT,
  addDays,
  browserTimezone,
  formatTime12h,
  isoDate,
  mondayOf,
  recordHrefForOccurrence,
} from "@/lib/schedule";
import { Card, Eyebrow, Icon, IconButton, SectionHeader, Tag } from "@/components/studio/primitives";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// The grid covers 7:00–21:00 in 30-minute rows; occurrences outside the
// band clamp to its edges (they still render, just pinned).
const GRID_START_MINUTES = 7 * 60;
const GRID_END_MINUTES = 21 * 60;
const ROW_MINUTES = 30;
const GRID_ROWS = (GRID_END_MINUTES - GRID_START_MINUTES) / ROW_MINUTES;

const REMINDER_OPTIONS = [
  { value: "0", label: "Off" },
  { value: "1", label: "1 hour before" },
  { value: "3", label: "3 hours before" },
  { value: "24", label: "24 hours before" },
  { value: "48", label: "48 hours before" },
];

const minutesOf = (timeLocal) => {
  const [h, m] = timeLocal.split(":").map(Number);
  return h * 60 + m;
};

/** Grid row span for an occurrence, clamped into the visible band. */
const rowsFor = (occ) => {
  const start = Math.max(GRID_START_MINUTES, Math.min(minutesOf(occ.time_local), GRID_END_MINUTES - ROW_MINUTES));
  const startRow = Math.floor((start - GRID_START_MINUTES) / ROW_MINUTES) + 1;
  const span = Math.max(1, Math.min(Math.round(occ.duration_minutes / ROW_MINUTES), GRID_ROWS - startRow + 1));
  return { startRow, span };
};

const EMPTY_FORM = {
  targetId: "",
  weekday: "1",
  oneOff: false,
  oneOffDate: "",
  time: "16:00",
  duration: "30",
  startsOn: "",
  endsOn: "",
  reminder: "24",
  programId: "", // "" = Solo lesson (Spec 12 WP1 attribution)
};

const SchedulePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  // Hans staff switched no-show marking off platform-wide — hide the action
  // rather than offer one that only 403s. Defaults on when /auth/me hasn't
  // landed (see featureFlags.noShowEnabled).
  const canMarkNoShow = noShowEnabled(user);
  // Scheduling is studio-layer (I7). Only AUTHORING is hidden — the week grid,
  // the occurrence dialog and the booking queue all still render, so a teacher
  // on the free plan keeps seeing every lesson they already scheduled.
  const hasStudioLayer = studioLayerEnabled(user);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [occurrences, setOccurrences] = useState([]);
  const [slots, setSlots] = useState([]);
  // True when the account's schedule is mastered in Acuity (Spec 09). Reads stay
  // open — only the Hans-native authoring surface is hidden. `null` = the answer
  // hasn't landed yet: defaulting to false made an Acuity account paint New slot /
  // Add to calendar / Booking page and the whole Availability section, then swap
  // them for "Managed in Acuity" a moment later. Render a placeholder instead.
  const [externallyManaged, setExternallyManaged] = useState(null);
  const managementKnown = externallyManaged !== null;
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]); // Spec 12 WP1: attribution picker
  const [loading, setLoading] = useState(true);
  const [slotDialog, setSlotDialog] = useState(null); // {mode: "create"|"edit", slot?}
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [selectedOcc, setSelectedOcc] = useState(null); // occurrence action dialog
  const [moveForm, setMoveForm] = useState(null); // {date, time} when moving
  const [bookings, setBookings] = useState([]); // Phase 2: pending reschedule requests
  const [windows, setWindows] = useState([]); // Phase 3: availability windows
  const [windowDialogOpen, setWindowDialogOpen] = useState(false);
  const [windowForm, setWindowForm] = useState({ weekday: "1", start: "15:00", end: "19:00", granularity: "30" });
  const [bookingUrl, setBookingUrl] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  // Booking-page profile: what visitors see next to the openings.
  const [profileForm, setProfileForm] = useState({ bio: "", lesson_types: [], photo_url: null });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const [attachPick, setAttachPick] = useState({}); // booking_id -> student_id for unmatched self-bookings
  const [slotAttachPick, setSlotAttachPick] = useState({}); // slot_id -> student_id for unmatched Acuity slots

  // Spec 09: slot lookup so an occurrence can tell if its slot is Acuity-sourced
  // (read-only: edit/skip/move hidden, Acuity badge shown, unmatched → attach).
  const slotsById = useMemo(() => Object.fromEntries(slots.map((s) => [s.slot_id, s])), [slots]);

  const fetchWindows = useCallback(async () => {
    try {
      const res = await api.get("/schedule/availability");
      setWindows(res.data?.windows || []);
    } catch {
      // Availability is additive — the page works without it.
    }
  }, []);

  useEffect(() => {
    fetchWindows();
  }, [fetchWindows]);

  // Spec 12 WP1: programs this instructor teaches in, for the slot attribution
  // picker. Gated on the marketplace flag (attribution is a program concept).
  useEffect(() => {
    if (!MARKETPLACE_ENABLED) return;
    let cancelled = false;
    api
      .get("/marketplace/my-programs")
      .then((res) => {
        if (!cancelled) setPrograms(res.data.programs || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const addWindow = async () => {
    try {
      await api.post("/schedule/availability", {
        weekday: Number(windowForm.weekday),
        start_time_local: windowForm.start,
        end_time_local: windowForm.end,
        timezone: browserTimezone(),
        slot_granularity_minutes: Number(windowForm.granularity),
      });
      toast.success("Availability added");
      setWindowDialogOpen(false);
      fetchWindows();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add the window"));
    }
  };

  const removeWindow = async (window_) => {
    try {
      await api.delete(`/schedule/availability/${window_.window_id}`);
      fetchWindows();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not remove the window"));
    }
  };

  const fetchBookingProfile = useCallback(async () => {
    try {
      const res = await api.get("/schedule/booking-profile");
      setProfileForm({
        bio: res.data?.bio || "",
        lesson_types: res.data?.lesson_types || [],
        photo_url: res.data?.photo_url || null,
      });
      setProfileLoaded(true);
    } catch {
      // Profile is additive — the link section works without it.
    }
  }, []);

  const openBookingDialog = async () => {
    setBookingDialogOpen(true);
    if (!profileLoaded) fetchBookingProfile();
    if (bookingUrl) return;
    try {
      const res = await api.post("/schedule/booking-token");
      setBookingUrl(res.data.url);
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not create the booking link"));
      setBookingDialogOpen(false);
    }
  };

  const toggleLessonType = (type) => {
    setProfileForm((f) => ({
      ...f,
      lesson_types: f.lesson_types.includes(type)
        ? f.lesson_types.filter((t) => t !== type)
        : [...f.lesson_types, type],
    }));
  };

  const saveBookingProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", {
        booking_bio: profileForm.bio.trim() || null,
        booking_lesson_types: profileForm.lesson_types,
      });
      toast.success("Booking page updated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save your booking profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadBookingPhoto = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPEG, PNG, or WebP image");
      return;
    }
    setUploadingPhoto(true);
    try {
      const res = await api.post("/schedule/booking-profile/photo", { content_type: file.type });
      // Bare axios: the presigned S3 URL must not get the API client's auth headers.
      await axios.put(res.data.upload_url, file, { headers: { "Content-Type": file.type } });
      setProfileForm((f) => ({ ...f, photo_url: res.data.photo_url }));
      toast.success("Photo uploaded");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not upload the photo"));
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const removeBookingPhoto = async () => {
    try {
      await api.delete("/schedule/booking-profile/photo");
      setProfileForm((f) => ({ ...f, photo_url: null }));
      toast.success("Photo removed");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not remove the photo"));
    }
  };

  const fetchBookings = useCallback(async () => {
    try {
      const res = await api.get("/schedule/bookings");
      setBookings(res.data?.bookings || []);
    } catch {
      // Best-effort — the week view works without the requests rail.
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const resolveBooking = async (booking, action) => {
    if (
      action === "confirm" &&
      booking.kind === "self_booking" &&
      !booking.student_id &&
      !attachPick[booking.booking_id]
    ) {
      toast.error("Pick which student this booking is for first");
      return;
    }
    try {
      const payload =
        action === "confirm" && booking.kind === "self_booking" && !booking.student_id
          ? { student_id: attachPick[booking.booking_id] }
          : {};
      await api.post(`/schedule/bookings/${booking.booking_id}/${action}`, payload);
      toast.success(action === "confirm" ? "Reschedule approved — the student was notified" : "Request declined");
      fetchBookings();
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not update the request"));
    }
  };

  const markNoShow = async (occ) => {
    try {
      await api.post(`/schedule/occurrences/${occ.slot_id}/${occ.date}/no-show`);
      toast.success("Marked as a no-show");
      setSelectedOcc(null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not mark the no-show"));
    }
  };

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekStart, i))),
    [weekStart]
  );

  const fetchWeek = useCallback(async (signal) => {
    try {
      const from = weekDates[0];
      const to = weekDates[6];
      const res = await api.get(`/schedule?from=${from}&to=${to}`, signal ? { signal } : {});
      setOccurrences(res.data.occurrences || []);
      setSlots(res.data.slots || []);
      // Spec 09: an Acuity-mapped account is read-only in Hans. Hide the whole
      // authoring surface rather than offer actions that can only 403.
      setExternallyManaged(!!res.data.externally_managed);
    } catch (error) {
      if (!signal?.aborted) {
        toast.error(getErrorMessage(error, "Failed to load schedule"));
        // The load failed, so we'll never learn which surface this account gets.
        // Fall back to the Hans-native one rather than leaving it hidden forever.
        setExternallyManaged((prev) => (prev === null ? false : prev));
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [weekDates]);

  useEffect(() => {
    const controller = new AbortController();
    fetchWeek(controller.signal);
    return () => controller.abort();
  }, [fetchWeek]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      api.get("/students", { signal: controller.signal }),
      api.get("/classes", { signal: controller.signal }).catch(() => ({ data: [] })),
    ])
      .then(([s, c]) => {
        setStudents(s.data || []);
        setClasses(c.data || []);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const byDate = useMemo(() => {
    const map = new Map(weekDates.map((d) => [d, []]));
    for (const occ of occurrences) {
      if (map.has(occ.effective_date)) map.get(occ.effective_date).push(occ);
    }
    return map;
  }, [occurrences, weekDates]);

  const todayIso = isoDate(new Date());

  // ── Slot create / edit ──

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, startsOn: todayIso });
    setSlotDialog({ mode: "create" });
  };

  const openEdit = (slot) => {
    setForm({
      targetId: slot.student_id ? `stu:${slot.student_id}` : `cls:${slot.class_id}`,
      weekday: String(slot.weekday ?? 1),
      oneOff: !!slot.one_off_date,
      oneOffDate: slot.one_off_date || "",
      time: slot.start_time_local,
      duration: String(slot.duration_minutes),
      startsOn: slot.starts_on,
      endsOn: slot.ends_on || "",
      reminder: String(slot.reminder_hours_before ?? 24),
      programId: slot.program_id || "",
    });
    setSlotDialog({ mode: "edit", slot });
  };

  const saveSlot = async () => {
    if (slotDialog.mode === "create" && !form.targetId) {
      toast.error("Choose a student or class");
      return;
    }
    if (form.oneOff && !form.oneOffDate) {
      toast.error("Pick a date for the one-off lesson");
      return;
    }
    setSaving(true);
    try {
      if (slotDialog.mode === "create") {
        const [kind, id] = form.targetId.split(":");
        const payload = {
          [kind === "stu" ? "student_id" : "class_id"]: id,
          start_time_local: form.time,
          timezone: browserTimezone(),
          duration_minutes: Number(form.duration),
          starts_on: form.oneOff ? form.oneOffDate : form.startsOn || todayIso,
          reminder_hours_before: Number(form.reminder),
        };
        if (form.oneOff) payload.one_off_date = form.oneOffDate;
        else {
          payload.weekday = Number(form.weekday);
          if (form.endsOn) payload.ends_on = form.endsOn;
        }
        if (MARKETPLACE_ENABLED && form.programId) payload.program_id = form.programId;
        await api.post("/schedule/slots", payload);
        toast.success("Slot added");
      } else {
        const slot = slotDialog.slot;
        const payload = {
          start_time_local: form.time,
          duration_minutes: Number(form.duration),
          reminder_hours_before: Number(form.reminder),
        };
        if (!slot.one_off_date) {
          payload.weekday = Number(form.weekday);
          if (form.endsOn) payload.ends_on = form.endsOn;
        }
        // Spec 12 WP1: set/clear the attributed program (null clears it).
        if (MARKETPLACE_ENABLED && form.programId !== (slot.program_id || "")) {
          payload.program_id = form.programId || null;
        }
        await api.patch(`/schedule/slots/${slot.slot_id}`, payload);
        toast.success("Slot updated");
      }
      setSlotDialog(null);
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save the slot"));
    } finally {
      setSaving(false);
    }
  };

  const deleteSlot = async (slot) => {
    if (!window.confirm("Delete this slot? Past recorded lessons keep their link to it.")) return;
    try {
      await api.delete(`/schedule/slots/${slot.slot_id}`);
      toast.success("Slot deleted");
      setSlotDialog(null);
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete the slot"));
    }
  };

  // ── Occurrence exceptions ──

  const skipOccurrence = async (occ) => {
    try {
      await api.post(`/schedule/slots/${occ.slot_id}/exceptions`, { date: occ.date, kind: "cancel" });
      toast.success("Occurrence skipped — students are being notified");
      setSelectedOcc(null);
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not skip this occurrence"));
    }
  };

  // Spec 09: attach a roster student to an unmatched Acuity slot (the one
  // write Hans allows on an acuity slot — it doesn't touch the Acuity booking).
  const attachStudentToSlot = async (slotId) => {
    const studentId = slotAttachPick[slotId];
    if (!studentId) {
      toast.error("Pick which student this lesson is for first");
      return;
    }
    try {
      await api.post(`/schedule/slots/${slotId}/attach-student`, { student_id: studentId });
      toast.success("Student attached");
      setSelectedOcc(null);
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not attach the student"));
    }
  };

  const moveOccurrence = async (occ) => {
    if (!moveForm?.date || !moveForm?.time) {
      toast.error("Pick the new date and time");
      return;
    }
    try {
      await api.post(`/schedule/slots/${occ.slot_id}/exceptions`, {
        date: occ.date,
        kind: "move",
        moved_to_date: moveForm.date,
        moved_to_time_local: moveForm.time,
      });
      toast.success("Occurrence moved");
      setSelectedOcc(null);
      setMoveForm(null);
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not move this occurrence"));
    }
  };

  const undoException = async (occ) => {
    try {
      await api.delete(`/schedule/exceptions/${occ.exception_id}`);
      toast.success("Back to the regular time");
      setSelectedOcc(null);
      fetchWeek();
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not undo"));
    }
  };

  const weekLabel = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  return (
    <div className="mx-auto max-w-[1180px] px-6 pb-32 pt-11 md:px-12" data-testid="schedule-page">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Weekly lessons</Eyebrow>
          <h1 className="mt-1.5 font-serif text-[44px] font-normal leading-none tracking-[-0.02em] text-[var(--text-primary)]">
            Schedule
          </h1>
        </div>
        {!managementKnown ? (
          // Placeholder while /schedule answers — same height as either surface,
          // so settling on one doesn't shift the header.
          <div
            aria-hidden="true"
            data-testid="schedule-actions-placeholder"
            className="skeleton h-[38px] w-[300px] rounded-full"
          />
        ) : externallyManaged ? (
          <div
            data-testid="externally-managed-note"
            className="rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-2 font-sans text-sm text-[var(--text-tertiary)]"
          >
            Managed in Acuity
          </div>
        ) : !hasStudioLayer ? (
          // Reads stay open, so the week grid below still shows every lesson
          // this instructor already has (I7) — only the authoring actions go.
          // An upgrade link rather than a disabled button: a control that can
          // only ever refuse is worse than one that leads somewhere.
          <Link
            to="/settings"
            data-testid="studio-layer-upsell"
            className="flex items-center gap-2 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-faint)] px-4 py-2 font-sans text-sm text-[var(--text-secondary)] transition-colors hover:bg-[var(--accent-yellow-dim)] active:scale-95"
          >
            <Icon name="calendar" size={15} />
            Scheduling is part of {tierDisplayName("studio")}
          </Link>
        ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openBookingDialog}
            data-testid="booking-link-button"
            className="flex items-center gap-2 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-2 font-sans text-sm text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)]"
          >
            <Icon name="link" size={15} />
            Booking page
          </button>
          <button
            type="button"
            onClick={openCreate}
            data-testid="new-slot-button"
            className="flex items-center gap-2 rounded-full bg-[var(--accent-yellow)] px-4 py-2 font-sans text-sm font-semibold text-[var(--text-inverse)]"
          >
            <Icon name="plus" size={15} strokeWidth={2.2} className="text-[var(--text-inverse)]" />
            New slot
          </button>
        </div>
        )}
      </div>

      {/* Week navigation */}
      <div className="mb-4 flex items-center gap-3">
        <IconButton icon="chevronLeft" ariaLabel="Previous week" onPress={() => setWeekStart((w) => addDays(w, -7))} data-testid="week-prev" />
        <IconButton icon="chevronRight" ariaLabel="Next week" onPress={() => setWeekStart((w) => addDays(w, 7))} data-testid="week-next" />
        <button
          type="button"
          onClick={() => setWeekStart(mondayOf(new Date()))}
          className="rounded-full border border-[var(--studio-divider)] px-3 py-1.5 font-sans text-[13px] text-[var(--text-secondary)]"
        >
          Today
        </button>
        <span className="font-mono text-sm text-[var(--text-tertiary)]" data-testid="week-label">{weekLabel}</span>
      </div>

      {/* Week grid */}
      <Card padding={0} className="overflow-x-auto">
        <div className="min-w-[760px]">
          {/* Day headers */}
          <div className="grid border-b border-[var(--studio-divider)]" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
            <div />
            {weekDates.map((d, i) => (
              <div key={d} className="px-2 py-3 text-center">
                <div className="font-sans text-[12px] uppercase tracking-wide text-[var(--text-tertiary)]">{WEEKDAYS_SHORT[i]}</div>
                <div
                  className={[
                    "mx-auto mt-1 flex h-7 w-7 items-center justify-center rounded-full font-mono text-[13px]",
                    d === todayIso ? "bg-[var(--accent-yellow)] font-bold text-[var(--text-inverse)]" : "text-[var(--text-secondary)]",
                  ].join(" ")}
                >
                  {Number(d.slice(8))}
                </div>
              </div>
            ))}
          </div>

          {/* Time rows × day columns */}
          <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
            {/* Hour gutter */}
            <div className="grid" style={{ gridTemplateRows: `repeat(${GRID_ROWS}, 26px)` }}>
              {Array.from({ length: GRID_ROWS }, (_, r) => {
                const minutes = GRID_START_MINUTES + r * ROW_MINUTES;
                const onHour = minutes % 60 === 0;
                return (
                  <div key={r} className="relative">
                    {onHour && (
                      <span className="absolute -top-2 right-2 font-mono text-[11px] text-[var(--text-quaternary)]">
                        {formatTime12h(`${String(minutes / 60).padStart(2, "0")}:00`).replace(":00", "")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {weekDates.map((d) => (
              <div
                key={d}
                data-testid={`day-col-${d}`}
                className="relative grid border-l border-[var(--studio-divider)]"
                style={{ gridTemplateRows: `repeat(${GRID_ROWS}, 26px)` }}
              >
                {/* Hour lines */}
                {Array.from({ length: GRID_ROWS }, (_, r) => (
                  <div
                    key={r}
                    className={(GRID_START_MINUTES + r * ROW_MINUTES) % 60 === 0 ? "border-t border-[var(--studio-divider)]" : ""}
                  />
                ))}
                {/* Occurrences */}
                {(byDate.get(d) || []).map((occ) => {
                  const { startRow, span } = rowsFor(occ);
                  const cancelled = occ.status === "cancelled";
                  return (
                    <button
                      key={`${occ.slot_id}-${occ.date}`}
                      type="button"
                      onClick={() => { setSelectedOcc(occ); setMoveForm(null); }}
                      data-testid={`occurrence-${occ.slot_id}-${occ.date}`}
                      style={{ gridRow: `${startRow} / span ${span}` }}
                      className={[
                        "absolute inset-x-1 z-10 flex flex-col justify-start overflow-hidden rounded-[8px] border px-2 py-1 text-left",
                        cancelled
                          ? "border-[var(--studio-divider)] bg-[var(--studio-raised)] opacity-50"
                          : "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]",
                      ].join(" ")}
                    >
                      <span className={["truncate font-sans text-[12px] font-medium text-[var(--text-primary)]", cancelled ? "line-through" : ""].join(" ")}>
                        {occ.display_name || slotsById[occ.slot_id]?.acuity_client_name || "Lesson"}
                      </span>
                      <span className="font-mono text-[11px] text-[var(--text-tertiary)]">
                        {formatTime12h(occ.time_local)}
                        {occ.status === "moved" ? " · moved" : ""}
                        {occ.lesson_id ? " · ✓" : ""}
                        {slotsById[occ.slot_id]?.source === "acuity" && (
                          <span
                            className="ml-1 text-[var(--accent-yellow-text)]"
                            title="Scheduled in Acuity"
                            data-testid={`acuity-badge-${occ.slot_id}-${occ.date}`}
                          >
                            · Acuity
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {loading && <div className="skeleton mt-4 h-10 rounded-xl" />}
      {!loading && slots.length === 0 && (
        <Card className="mt-5 text-center" style={{ padding: "40px 20px" }}>
          <Icon name="calendar" size={34} className="mx-auto mb-3 text-[var(--text-quaternary)]" />
          <p className="font-sans text-sm text-[var(--text-tertiary)]">
            No weekly slots yet — add your students' regular lesson times to see your week at a glance.
          </p>
        </Card>
      )}

      {/* Phase 2: pending reschedule requests */}
      {bookings.length > 0 && (
        <div className="mt-9">
          <SectionHeader>Reschedule requests</SectionHeader>
          <Card padding={6} data-testid="requests-section">
            {bookings.map((b, i) => (
              <div
                key={b.booking_id}
                data-testid={`booking-${b.booking_id}`}
                className={[
                  "flex flex-wrap items-center gap-3 px-4 py-3.5",
                  i < bookings.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
                ].join(" ")}
              >
                <Icon name={b.kind === "self_booking" ? "sparkle" : "clock"} size={17} className="text-[var(--accent-yellow-text)]" />
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">
                    {b.student_name || b.requester_name || "Student"}
                    {b.kind === "self_booking" && (
                      <span className="ml-2 rounded-full border border-[var(--studio-divider)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">
                        New booking
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[13px] text-[var(--text-tertiary)]">
                    {b.kind === "self_booking"
                      ? `${b.proposed_date} ${formatTime12h(b.proposed_time_local)}${b.lesson_type ? ` · ${LESSON_TYPE_LABELS[b.lesson_type] || b.lesson_type}` : ""}${b.requester_email ? ` · ${b.requester_email}` : ""}`
                      : `${b.origin_date} → ${b.proposed_date} ${formatTime12h(b.proposed_time_local)}`}
                    {b.note ? ` · “${b.note}”` : ""}
                  </div>
                  {b.kind === "self_booking" && !b.student_id && (
                    <select
                      value={attachPick[b.booking_id] || ""}
                      onChange={(e) => setAttachPick((p) => ({ ...p, [b.booking_id]: e.target.value }))}
                      data-testid={`attach-student-${b.booking_id}`}
                      className="mt-2 rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-2.5 py-1.5 font-sans text-[13px] text-[var(--text-primary)]"
                    >
                      <option value="">Attach to a student…</option>
                      {students.map((s) => (
                        <option key={s.student_id} value={s.student_id}>
                          {`${s.first_name} ${s.last_name}`.trim()}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => resolveBooking(b, "confirm")}
                    data-testid={`approve-${b.booking_id}`}
                    className="rounded-full bg-[var(--accent-yellow)] px-4 py-1.5 font-sans text-[13px] font-semibold text-[var(--text-inverse)]"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => resolveBooking(b, "decline")}
                    data-testid={`decline-${b.booking_id}`}
                    className="rounded-full border border-[var(--studio-divider)] px-4 py-1.5 font-sans text-[13px] text-[var(--text-secondary)]"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Phase 3: availability for the public booking page. Hidden on an
          Acuity-managed account — availability CRUD and the booking token both
          403 there (Spec 09 §5), and openings are offered in Acuity instead.
          Hidden on a free plan too (I7): the booking page is studio-layer, and
          every write in this section would 403. Unlike the week grid above
          there is nothing here worth showing read-only — availability windows
          exist only to feed a page that no longer resolves. */}
      {managementKnown && !externallyManaged && hasStudioLayer && (
      <div className="mt-9">
        <SectionHeader
          action="+ Add window"
          onAction={() => setWindowDialogOpen(true)}
          actionTestId="add-window-button"
        >
          Availability
        </SectionHeader>
        <Card padding={6} data-testid="availability-section">
          {windows.length === 0 ? (
            <div className="px-6 py-6 text-center font-sans text-sm text-[var(--text-tertiary)]">
              Add the hours you're open to teach and share your booking page — openings are
              your windows minus lessons already on the books.
            </div>
          ) : (
            windows.map((w, i) => (
              <div
                key={w.window_id}
                data-testid={`window-row-${w.window_id}`}
                className={[
                  "flex items-center gap-4 px-4 py-3",
                  i < windows.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
                ].join(" ")}
              >
                <Icon name="clock" size={16} className="text-[var(--accent-yellow-text)]" />
                <div className="flex-1 font-sans text-[14px] text-[var(--text-primary)]">
                  {WEEKDAYS[w.weekday]}{" "}
                  <span className="font-mono text-[13px] text-[var(--text-tertiary)]">
                    {formatTime12h(w.start_time_local)} – {formatTime12h(w.end_time_local)} · {w.slot_granularity_minutes}-min lessons
                  </span>
                </div>
                <IconButton icon="trash" ariaLabel="Remove window" onPress={() => removeWindow(w)} data-testid={`remove-window-${w.window_id}`} />
              </div>
            ))
          )}
        </Card>
      </div>
      )}

      {/* Slots list (management) */}
      {slots.length > 0 && (
        <div className="mt-9">
          <SectionHeader>Weekly slots</SectionHeader>
          <Card padding={6}>
            {slots.map((slot, i) => {
              const target =
                students.find((s) => s.student_id === slot.student_id) ||
                classes.find((c) => c.class_id === slot.class_id);
              const isAcuity = slot.source === "acuity";
              const name = slot.class_id
                ? target?.name || "Class"
                : `${target?.first_name || ""} ${target?.last_name || ""}`.trim()
                  || slot.acuity_client_name
                  || "Student";
              const dayLabel = slot.one_off_date ? slot.one_off_date : WEEKDAYS[slot.weekday];
              return (
                <div
                  key={slot.slot_id}
                  data-testid={`slot-row-${slot.slot_id}`}
                  className={[
                    "flex items-center gap-4 px-4 py-3.5",
                    i < slots.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
                  ].join(" ")}
                >
                  <Icon name={slot.class_id ? "users" : "user"} size={17} className="text-[var(--accent-yellow-text)]" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-sans text-[15px] font-medium text-[var(--text-primary)]">{name}</div>
                    <div className="font-mono text-[13px] text-[var(--text-tertiary)]">
                      {dayLabel} · {formatTime12h(slot.start_time_local)} · {slot.duration_minutes} min
                      {slot.one_off_date ? " · one-off" : ""}
                      {isAcuity ? " · Acuity" : ""}
                    </div>
                  </div>
                  {isAcuity ? (
                    <span data-testid={`acuity-slot-tag-${slot.slot_id}`}>
                      <Tag accent>Acuity</Tag>
                    </span>
                  ) : (
                    <IconButton icon="edit" ariaLabel={`Edit slot for ${name}`} onPress={() => openEdit(slot)} data-testid={`edit-slot-${slot.slot_id}`} />
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* Slot create/edit dialog */}
      <Dialog open={!!slotDialog} onOpenChange={(open) => !open && setSlotDialog(null)}>
        <DialogContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal tracking-[-0.01em]">
              {slotDialog?.mode === "create" ? "New lesson slot" : "Edit slot"}
            </DialogTitle>
            <DialogDescription>
              {slotDialog?.mode === "create"
                ? "A weekly time (or one-off lesson) for a student or class."
                : "Change the time, length, or reminder. The student/class can't change."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {slotDialog?.mode === "create" && (
              <>
                <Select value={form.targetId} onValueChange={(v) => setForm((f) => ({ ...f, targetId: v }))}>
                  <SelectTrigger className="h-12 w-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="slot-target-select">
                    <SelectValue placeholder="Student or class…" />
                  </SelectTrigger>
                  <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                    {students.map((s) => (
                      <SelectItem key={s.student_id} value={`stu:${s.student_id}`}>
                        {`${s.first_name} ${s.last_name}`.trim()}
                      </SelectItem>
                    ))}
                    {classes.map((c) => (
                      <SelectItem key={c.class_id} value={`cls:${c.class_id}`}>
                        {c.name} (class)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 font-sans text-sm text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={form.oneOff}
                    onChange={(e) => setForm((f) => ({ ...f, oneOff: e.target.checked }))}
                    data-testid="one-off-toggle"
                  />
                  One-off lesson (not weekly)
                </label>
              </>
            )}
            {form.oneOff ? (
              <label className="block font-sans text-sm text-[var(--text-secondary)]">
                Date
                <Input
                  type="date"
                  value={form.oneOffDate}
                  onChange={(e) => setForm((f) => ({ ...f, oneOffDate: e.target.value }))}
                  className="mt-1 border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]"
                  data-testid="one-off-date"
                />
              </label>
            ) : (
              <Select value={form.weekday} onValueChange={(v) => setForm((f) => ({ ...f, weekday: v }))}>
                <SelectTrigger className="h-12 w-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="slot-weekday-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                  {WEEKDAYS.map((day, i) => (
                    <SelectItem key={day} value={String(i)}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block font-sans text-sm text-[var(--text-secondary)]">
                Time
                <Input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                  className="mt-1 border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]"
                  data-testid="slot-time-input"
                />
              </label>
              <label className="block font-sans text-sm text-[var(--text-secondary)]">
                Minutes
                <Input
                  type="number"
                  min="15"
                  max="240"
                  step="5"
                  value={form.duration}
                  onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                  className="mt-1 border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]"
                  data-testid="slot-duration-input"
                />
              </label>
            </div>
            <div>
              <span className="font-sans text-sm text-[var(--text-secondary)]">Remind the student</span>
              <Select value={form.reminder} onValueChange={(v) => setForm((f) => ({ ...f, reminder: v }))}>
                <SelectTrigger className="mt-1 h-11 w-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="slot-reminder-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                  {REMINDER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {MARKETPLACE_ENABLED && programs.length > 0 && (
              <div>
                <span className="font-sans text-sm text-[var(--text-secondary)]">Program</span>
                <Select
                  value={form.programId || "__solo__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, programId: v === "__solo__" ? "" : v }))}
                >
                  <SelectTrigger className="mt-1 h-11 w-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="slot-program-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                    <SelectItem value="__solo__">Solo lesson</SelectItem>
                    {programs.map((p) => (
                      <SelectItem key={p.program_id} value={p.program_id}>
                        {p.title}{p.org_name ? ` · ${p.org_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              {slotDialog?.mode === "edit" ? (
                <button
                  type="button"
                  onClick={() => deleteSlot(slotDialog.slot)}
                  className="font-sans text-sm text-[var(--state-danger)] underline"
                  data-testid="delete-slot-button"
                >
                  Delete slot
                </button>
              ) : <span />}
              <button
                type="button"
                onClick={saveSlot}
                disabled={saving}
                data-testid="save-slot-button"
                className="rounded-full bg-[var(--accent-yellow)] px-5 py-2.5 font-sans text-sm font-semibold text-[var(--text-inverse)] disabled:opacity-50"
              >
                {saving ? "Saving…" : slotDialog?.mode === "create" ? "Add slot" : "Save"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Occurrence action dialog */}
      <Dialog open={!!selectedOcc} onOpenChange={(open) => { if (!open) { setSelectedOcc(null); setMoveForm(null); } }}>
        <DialogContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-sm">
          {selectedOcc && (() => {
            const occSlot = slotsById[selectedOcc.slot_id] || {};
            const isAcuity = occSlot.source === "acuity";
            const isUnmatchedAcuity = isAcuity && !occSlot.student_id;
            return (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-normal tracking-[-0.01em]">
                  {selectedOcc.display_name || occSlot.acuity_client_name || "Lesson"}
                </DialogTitle>
                <DialogDescription>
                  {selectedOcc.effective_date} · {formatTime12h(selectedOcc.time_local)} · {selectedOcc.duration_minutes} min
                  {selectedOcc.status !== "scheduled" ? ` · ${selectedOcc.status}` : ""}
                  {selectedOcc.note ? ` — ${selectedOcc.note}` : ""}
                </DialogDescription>
              </DialogHeader>
              {isAcuity && (
                <div
                  className="flex items-center gap-2 rounded-lg border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 font-sans text-[12px] text-[var(--text-secondary)]"
                  data-testid="acuity-occurrence-note"
                >
                  <Icon name="calendar" size={14} className="text-[var(--accent-yellow-text)]" />
                  Retained Acuity import — updates no longer sync to Hans. You can still record this lesson.
                </div>
              )}
              {isUnmatchedAcuity && (
                <div className="space-y-2 rounded-lg border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-3" data-testid="acuity-attach">
                  <p className="font-sans text-[12px] text-[var(--text-secondary)]">
                    Booked by {occSlot.acuity_client_name || "an Acuity client"} — attach the roster student so
                    recording links up.
                  </p>
                  <select
                    value={slotAttachPick[selectedOcc.slot_id] || ""}
                    onChange={(e) => setSlotAttachPick((p) => ({ ...p, [selectedOcc.slot_id]: e.target.value }))}
                    data-testid={`attach-slot-student-${selectedOcc.slot_id}`}
                    className="w-full rounded-lg border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
                  >
                    <option value="">Select a student…</option>
                    {students.map((s) => (
                      <option key={s.student_id} value={s.student_id}>
                        {`${s.first_name || ""} ${s.last_name || ""}`.trim() || "Student"}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => attachStudentToSlot(selectedOcc.slot_id)}
                    data-testid="attach-slot-confirm"
                    className="w-full rounded-full bg-[var(--accent-yellow)] py-2 font-sans text-sm font-semibold text-[var(--text-inverse)]"
                  >
                    Attach student
                  </button>
                </div>
              )}
              {moveForm ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="date"
                      value={moveForm.date}
                      onChange={(e) => setMoveForm((m) => ({ ...m, date: e.target.value }))}
                      className="border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]"
                      data-testid="move-date-input"
                    />
                    <Input
                      type="time"
                      value={moveForm.time}
                      onChange={(e) => setMoveForm((m) => ({ ...m, time: e.target.value }))}
                      className="border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]"
                      data-testid="move-time-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => moveOccurrence(selectedOcc)}
                    data-testid="confirm-move-button"
                    className="w-full rounded-full bg-[var(--accent-yellow)] py-2.5 font-sans text-sm font-semibold text-[var(--text-inverse)]"
                  >
                    Move lesson
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedOcc.status !== "cancelled" && !selectedOcc.lesson_id && (
                    <button
                      type="button"
                      onClick={() => navigate(recordHrefForOccurrence(selectedOcc))}
                      data-testid="occ-record-button"
                      className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-sm text-[var(--text-primary)]"
                    >
                      <Icon name="mic" size={16} className="text-[var(--accent-yellow-text)]" /> Record this lesson
                    </button>
                  )}
                  {selectedOcc.lesson_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/lesson/${selectedOcc.lesson_id}${selectedOcc.class_id ? "?kind=class" : ""}`)}
                      className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-sm text-[var(--text-primary)]"
                    >
                      <Icon name="check" size={16} className="text-[var(--accent-yellow-text)]" /> View recorded lesson
                    </button>
                  )}
                  {selectedOcc.pending_booking_id && (
                    <div className="rounded-xl border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-4 py-3 font-sans text-[13px] text-[var(--accent-yellow-text)]">
                      The student asked to move this lesson — see “Reschedule requests” below the grid.
                    </div>
                  )}
                  {canMarkNoShow &&
                    new Date(selectedOcc.start_at) < new Date() &&
                    selectedOcc.status !== "cancelled" &&
                    !selectedOcc.lesson_id && (
                      <button
                        type="button"
                        onClick={() => markNoShow(selectedOcc)}
                        data-testid="mark-no-show-button"
                        className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-sm text-[var(--text-primary)]"
                      >
                        <Icon name="alert" size={16} className="text-[var(--accent-yellow-text)]" /> Mark as no-show
                      </button>
                    )}
                  {/* Spec 09: acuity slots are read-only in Hans — no move/skip/undo.
                      I7: so are they on a free plan, since move/skip/undo all
                      write exceptions through `_require_scheduling`. The dialog
                      still OPENS and still shows the student, the time and the
                      program — reading an occurrence was never gated. */}
                  {!isAcuity && hasStudioLayer && (selectedOcc.exception_id ? (
                    <button
                      type="button"
                      onClick={() => undoException(selectedOcc)}
                      data-testid="undo-exception-button"
                      className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-sm text-[var(--text-primary)]"
                    >
                      <Icon name="refresh" size={16} className="text-[var(--accent-yellow-text)]" />
                      {selectedOcc.status === "cancelled" ? "Restore this lesson" : "Back to the regular time"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setMoveForm({ date: selectedOcc.effective_date, time: selectedOcc.time_local })}
                        data-testid="move-occurrence-button"
                        className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-sm text-[var(--text-primary)]"
                      >
                        <Icon name="clock" size={16} className="text-[var(--accent-yellow-text)]" /> Move this week's lesson
                      </button>
                      <button
                        type="button"
                        onClick={() => skipOccurrence(selectedOcc)}
                        data-testid="skip-occurrence-button"
                        className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-sm text-[var(--text-primary)]"
                      >
                        <Icon name="x" size={16} className="text-[var(--accent-yellow-text)]" /> Skip this week
                      </button>
                    </>
                  ))}
                </div>
              )}
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Availability window dialog */}
      <Dialog open={windowDialogOpen} onOpenChange={setWindowDialogOpen}>
        <DialogContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal tracking-[-0.01em]">Add availability</DialogTitle>
            <DialogDescription>
              A weekly span you're open to teach. Booked lessons and holds are subtracted automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={windowForm.weekday} onValueChange={(v) => setWindowForm((f) => ({ ...f, weekday: v }))}>
              <SelectTrigger className="h-11 w-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="window-weekday-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                {WEEKDAYS.map((day, i) => (
                  <SelectItem key={day} value={String(i)}>{day}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <label className="block font-sans text-sm text-[var(--text-secondary)]">
                From
                <Input type="time" value={windowForm.start} onChange={(e) => setWindowForm((f) => ({ ...f, start: e.target.value }))} className="mt-1 border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="window-start-input" />
              </label>
              <label className="block font-sans text-sm text-[var(--text-secondary)]">
                Until
                <Input type="time" value={windowForm.end} onChange={(e) => setWindowForm((f) => ({ ...f, end: e.target.value }))} className="mt-1 border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]" data-testid="window-end-input" />
              </label>
            </div>
            <div>
              <span className="font-sans text-sm text-[var(--text-secondary)]">Lesson length</span>
              <Select value={windowForm.granularity} onValueChange={(v) => setWindowForm((f) => ({ ...f, granularity: v }))}>
                <SelectTrigger className="mt-1 h-11 w-full border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-primary)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                  {["30", "45", "60"].map((m) => (
                    <SelectItem key={m} value={m}>{m} minutes</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={addWindow}
                data-testid="save-window-button"
                className="rounded-full bg-[var(--accent-yellow)] px-5 py-2.5 font-sans text-sm font-semibold text-[var(--text-inverse)]"
              >
                Add window
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking link dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal tracking-[-0.01em]">Your booking page</DialogTitle>
            <DialogDescription>
              Share this link with students and parents — they pick from your open times, and every
              request still comes to you for approval. Reopening this dialog in a new session mints a
              fresh link and revokes the old one.
            </DialogDescription>
          </DialogHeader>
          {bookingUrl ? (
            <div className="flex items-center gap-2">
              <Input readOnly value={bookingUrl} className="border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] font-mono text-[12px] text-[var(--text-primary)]" data-testid="booking-url" />
              <IconButton
                icon="copy"
                ariaLabel="Copy booking link"
                onPress={() => {
                  navigator.clipboard?.writeText(bookingUrl);
                  toast.success("Link copied");
                }}
              />
            </div>
          ) : (
            <div className="skeleton h-10 rounded-lg" />
          )}

          {/* What visitors see: photo, bio, offered lesson types */}
          <div className="mt-2 space-y-4 border-t border-[var(--studio-divider-strong)] pt-4" data-testid="booking-profile-section">
            <p className="font-sans text-sm font-semibold text-[var(--text-primary)]">What visitors see</p>

            <div className="flex items-center gap-3">
              {profileForm.photo_url ? (
                <img
                  src={profileForm.photo_url}
                  alt="Your booking page photo"
                  data-testid="booking-profile-photo"
                  className="h-14 w-14 rounded-full border border-[var(--studio-divider-strong)] object-cover"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-[var(--studio-divider-strong)] text-[var(--text-quaternary)]">
                  <Icon name="user" size={20} />
                </div>
              )}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="booking-photo-input"
                onChange={(e) => uploadBookingPhoto(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="booking-photo-upload"
                className="rounded-full border border-[var(--studio-divider-strong)] px-3.5 py-1.5 font-sans text-[12px] text-[var(--text-secondary)] disabled:opacity-50"
              >
                {uploadingPhoto ? "Uploading…" : profileForm.photo_url ? "Change photo" : "Add a photo"}
              </button>
              {profileForm.photo_url && (
                <button
                  type="button"
                  onClick={removeBookingPhoto}
                  data-testid="booking-photo-remove"
                  className="font-sans text-[12px] text-[var(--text-tertiary)] underline"
                >
                  Remove
                </button>
              )}
            </div>

            <textarea
              value={profileForm.bio}
              onChange={(e) => setProfileForm((f) => ({ ...f, bio: e.target.value }))}
              placeholder="A short intro for students and parents — your background, teaching style, instruments…"
              rows={3}
              maxLength={1000}
              data-testid="booking-bio-input"
              className="w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3.5 py-2.5 font-sans text-sm text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]"
            />

            <div>
              <p className="mb-2 font-sans text-[12px] text-[var(--text-secondary)]">
                Lesson types you offer — visitors pick one when they book
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(LESSON_TYPE_LABELS).map(([type, label]) => {
                  const on = profileForm.lesson_types.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleLessonType(type)}
                      aria-pressed={on}
                      data-testid={`lesson-type-toggle-${type}`}
                      className={[
                        "rounded-full border px-3.5 py-1.5 font-sans text-[12px] transition-colors",
                        on
                          ? "border-[var(--accent-yellow)] bg-[var(--accent-yellow-dim)] text-[var(--text-primary)]"
                          : "border-[var(--studio-divider-strong)] text-[var(--text-secondary)]",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={saveBookingProfile}
              disabled={savingProfile}
              data-testid="save-booking-profile"
              className="rounded-full bg-[var(--accent-yellow)] px-5 py-2 font-sans text-[13px] font-semibold text-[var(--text-inverse)] disabled:opacity-50"
            >
              {savingProfile ? "Saving…" : "Save booking page"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-6">
        <Tag>Cancelled lessons stay visible, struck through, so the week reads honestly.</Tag>
      </div>
    </div>
  );
};

export default SchedulePage;
